import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import React, { useMemo, useRef } from 'react';
import { Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { reach } from 'yup';
import useBreakpoint from '../../hooks/useBreakpoint';
import useMembershipButton from '../../hooks/useMembershipButton';
import { updateProjectName } from '../../redux/actions';
import { JobListing, User } from '../../redux/rtk';
import { profileSelectors } from '../../redux/selectors/profile';
import { isFailed, isSuccess } from '../../redux/stateTools';
import routes from '../../routes/constants';
import text from '../../text';
import { formatDate } from '../../utilities/date';
import { isDemoJob, jobHasPointCloud } from '../../utilities/job';
import {
  PROJ_NAME_MAX_LENGTH,
  projectValidationSchema,
} from '../../validation';
import LinkButton from '../button/LinkButton';
import Icon from '../icon/Icon';
import Cell from '../table/Cell';
import OrderListingMenu from './OrderListingMenu';
import Status from './Status';
import { NON_EDITABLE_STATUSES } from './consts';

export const ORDER_LISTING_HEIGHT_PX = 76; // KEEP THIS in coordination with $order-listing-height scss variable

export const COLUMN = {
  DATE: 'date-column',
  NAME: 'name-column',
  ADDRESS: 'address-column',
  STATUS: 'status-column',
  FILE_MANAGER: 'file-manager-column',
  VIEW3D: 'view3D-column',
  ACTIONS: 'actions-column',
  ORDER_COUNT: 'order-count-column',
};

const BadgeCount = ({ count }: { count: number }) => {
  if (count <= 0) {
    return null;
  }
  return (
    <Badge className='badge-greyed badge--padded-top'>{`+ ${count}`}</Badge>
  );
};

interface OrderListingItemProps {
  job: JobListing;
  currentUser: User;
  isAdminOrigin: boolean;
  isStaff: boolean;
  style: React.CSSProperties;
}

const OrderListingItem: React.FC<OrderListingItemProps> = ({
  job,
  isAdminOrigin,
  isStaff,
  style = {},
}) => {
  const dispatch = useDispatch();
  const isUpgradeRecommended = useSelector(
    profileSelectors.getIsUpgradeRecommended
  );
  const is3dViewerAccessibleToUser = useSelector(
    profileSelectors.get3dViewerEnabled
  );
  const isAdmin = useSelector(profileSelectors.getIsAdmin);

  const { handleMembershipClick } = useMembershipButton();
  const is3dViewerLocked =
    jobHasPointCloud(job) && !is3dViewerAccessibleToUser && !isDemoJob(job);

  const isEditingEnabledForBkp = useBreakpoint(['sm', 'md', 'lg', 'xl']);

  const isPending = job.user_project?.invite_status === 'pending';

  const _3dViewerRoute = isAdminOrigin
    ? `${routes.view3DExternal.project(job.projectId)}?admin=true`
    : `${routes.view3DExternal.project(job.projectId)}`;

  const onUpdateProjectName = (projectName: string) =>
    dispatch(updateProjectName(job.projectId, projectName.trim(), isAdmin));

  const jobCountExceptCurrent = useMemo(() => {
    if (!job?.jobs) return 0;

    return job.jobs.length - 1;
  }, [job]);

  const _2dMapRoute = job?.id
    ? job.map2d_id
      ? routes.mp.map2d(job.map2d_id)
      : routes.order.job(job.id)
    : routes.mp.root;

  const view3dTooltipText = isUpgradeRecommended
    ? text('upgradeToView3dTooltip')
    : text('startTrialToView3dTooltip');

  const orderListingMenuRef = useRef<HTMLDivElement>(null);

  // returns the min-status - max-status of orders inside a project
  // job.jobs already sorted by status.order from projectListing.tsx
  const jobStatusesToShow = useMemo(() => {
    return job.jobs.length > 1
      ? (() => {
          const first = job.jobs[0];
          const last = job.jobs.at(-1);
          return first.status === last?.status ? [first] : [first, last];
        })()
      : job.jobs;
  }, [job.jobs]);

  const getUserDisplayName = (creator: User & { is_anonymous?: boolean }) => {
    if (!creator) return null;
    if (creator.is_anonymous) {
      return `(${text('anonymous')})`;
    }
    return creator.full_name || null;
  };

  const creatorName = getUserDisplayName(job.creator);

  return (
    !isEmpty(job) && (
      <>
        <tr
          style={style}
          className={classNames({
            'has-pending-invite': job.user_project?.invite_status === 'pending',
            'has-non-staff': job.has_customer && isStaff,
            'is-other': job.project?.is_other,
          })}
        >
          <Cell
            className={COLUMN.NAME}
            disabled={
              (NON_EDITABLE_STATUSES.includes(job.status) && !isStaff) ||
              !isEditingEnabledForBkp
            }
            isFailed={isFailed(job.ui?.updateState)}
            isSuccessful={isSuccess(job.ui?.updateState)}
            id={job.id}
            maxLength={PROJ_NAME_MAX_LENGTH}
            onSubmit={onUpdateProjectName}
            validationSchema={reach(projectValidationSchema, 'project.name')}
            value={
              isNull(job.project?.name) ? text('untitled') : job.project?.name
            }
          >
            <Status className='status-in-name' job={job} suffix={null} />
          </Cell>
          <td className={COLUMN.ADDRESS}>
            <div className='d-flex flex-column flex-xl-row align-items-xl-end'>
              <div>{job.full_address}</div>
            </div>
          </td>
          {/* Temporarily hide order count column: https://larki.atlassian.net/browse/LP-3543 */}
          {/* <td className={COLUMN.ORDER_COUNT}>
            <BadgeCount count={jobCountExceptCurrent} />
          </td> */}
          <td className={COLUMN.DATE}>
            <p className='mb-0'>{formatDate(job.created_at)}</p>
            <p className='mb-0'>{creatorName}</p>
          </td>
          <td className={COLUMN.DATE}>
            <p className='mb-0'>{formatDate(job.updated_at)}</p>
            <p className='mb-0'>
              {job.updater && job.updater.full_name
                ? job.updater.full_name
                : null}
            </p>
          </td>
          <td className={COLUMN.STATUS}>
            {jobStatusesToShow.map((_job, index) => {
              return (
                <React.Fragment key={job.id}>
                  <div className='d-flex flex-row align-items-center'>
                    <Status
                      job={_job as unknown as JobListing}
                      className={classNames('pb-2', {
                        'pt-2': index === 0,
                      })}
                      suffix={
                        index !== jobStatusesToShow.length - 1 ? '—' : null
                      }
                    />
                  </div>
                </React.Fragment>
              );
            })}
          </td>
          <td className={COLUMN.ACTIONS}>
            <div className='job-actions'>
              {/* @ts-expect-error: LinkButton is not typed */}
              <LinkButton
                className={classNames('table-icon-button', {
                  'star-container': is3dViewerLocked,
                })}
                tooltipProps={{
                  text: text(
                    is3dViewerLocked
                      ? view3dTooltipText
                      : jobHasPointCloud(job)
                        ? 'viewer3D'
                        : 'viewer3dUnavailable'
                  ),
                }}
                // A paywalled button stays clickable so it can open the
                // upsell; only a missing point cloud makes it dead, and that
                // holds for a Sample too.
                disabled={isPending || !jobHasPointCloud(job)}
                {...(!isPending
                  ? is3dViewerLocked
                    ? {
                        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
                          e.preventDefault();

                          handleMembershipClick({
                            redirectTo: {
                              path: _3dViewerRoute,
                              label: text('navigateToRoute', {
                                navigation: 'Continue',
                                routeName: text('viewer3D'),
                              }),
                            },
                          });
                        },
                      }
                    : jobHasPointCloud(job)
                      ? {
                          to: _3dViewerRoute,
                        }
                      : {}
                  : {})}
              >
                <Icon icon='3D-with-text' />
                {is3dViewerLocked ? (
                  <div className='lock-badge-container'>
                    <Icon icon='lock' />
                  </div>
                ) : null}
              </LinkButton>

              {/* @ts-expect-error: LinkButton is not typed */}
              <LinkButton
                disabled={isPending}
                tooltipProps={{ text: text('map2D') }}
                to={
                  job.lga
                    ? routes.order.root
                    : isAdminOrigin
                      ? `${_2dMapRoute}?admin=true`
                      : _2dMapRoute
                }
                className='table-icon-button'
              >
                <Icon icon='2D-with-text' />
              </LinkButton>

              {/* @ts-expect-error: LinkButton is not typed */}
              <LinkButton
                disabled={isPending}
                tooltipProps={{ text: text('fileManager') }}
                to={
                  isAdminOrigin
                    ? `${routes.fileManager.viewProject(
                        job.project_id
                      )}?admin=true`
                    : `${routes.fileManager.viewProject(job.project_id)}`
                }
                className='table-icon-button'
              >
                <Icon icon='file-with-text' />
              </LinkButton>

              <OrderListingMenu job={job} ref={orderListingMenuRef} />
            </div>
          </td>
        </tr>
        {/* {isPending && <InviteRow job={job} />} */}
      </>
    )
  );
};

export default OrderListingItem;
